import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useInView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'
import moment from 'moment'
import { ALL_HOTEL_TYPES } from 'constants/offer'
import { queryKeyTypeaheadId } from 'constants/url'
import GeoContext from 'contexts/geoContext'
import { getOffersById } from 'api/offer'
import { uniqueBy } from 'lib/array/arrayUtils'
import { cloneOccupancies, isValidOccupancy } from 'lib/offer/occupancyUtils'
import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import { encodeSearchParams } from 'lib/search/searchUtils'
import { serialiseOccupancy } from 'lib/search/occupancy'
import uuidV4 from 'lib/string/uuidV4Utils'
import { buildUrl } from 'lib/url/searchUrlUtils'
import { getOfferDetail } from 'tripPlanner/api'
import type { HotelSearchSection, TripItineraryData } from 'tripPlanner/api/types'
import { useSearchPlace } from 'tripPlanner/hooks/api/place'
import { OFFER_DETAIL_STALE_TIME } from 'tripPlanner/hooks/api/queryConstants'
import { useOfferBookingState } from 'tripPlanner/hooks/useOfferBookingState'
import useTripPlannerOfferListTracking from 'tripPlanner/hooks/useTripPlannerOfferListTracking'
import * as OfferDetailKeys from 'tripPlanner/hooks/reactQueryKeys/offerDetail'
import * as OfferKeys from 'tripPlanner/hooks/reactQueryKeys/offers'
import {
  getOfferItineraryDayRange,
  getOfferSavedBooking,
  getSavedHotelItemsFromStops,
  isOfferBookedInItinerary,
  type SavedTripHotelItem,
} from 'tripPlanner/utils/chatItinerary'
import { resolveSearchDefaults } from 'tripPlanner/utils/resolveSearchDefaults'
import { buildHotelRecentSearchUpsertBody } from 'tripPlanner/utils/tripChatRecentSearchPayload'
import usePersistTripChatRecentSearch from 'tripPlanner/hooks/usePersistTripChatRecentSearch'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import type { ListingDrawerSearchSubmitParams } from '../ListingDrawerSearchControls'
import { buildHotelSearchSubmittedActivityEntry } from './buildHotelSearchSubmittedActivityEntry'
import { buildLastHotelSearchCommittedStateEntry } from './buildLastHotelSearchCommittedStateEntry'
import useOfferDetails from '../../hooks/useOfferDetails'
import useOfferReviews from '../../hooks/useOfferReviews'
import { mapOfferDetailToDrawerHotel, mapOfferToDrawerHotelTileFeatures, mergeDrawerHotelWithOfferDetail } from './mapOfferDetailToDrawerHotel'
import { composeLocationString, mapDrawerHotelPricingFields, mapGeoLocation, mapImages, mapPrice } from './mapToDrawerHotel'
import type { DrawerHotel, HotelListingDrawerProps } from './types'
import useHotelSearch from './useHotelSearch'
import {
  areHotelSearchDateSelectionsEqual,
  getDefaultHotelSearchDateSelection,
  getHotelSearchDateParams,
  getHotelSearchDateSelectionKey,
} from './hotelSearchDates'
import {
  isListingDrawerHotelDateSelection,
  type ListingDrawerHotelDateSelection,
} from '../ListingDrawerSearchDates'

const DEFAULT_OCCUPANCIES: Array<App.Occupants> = [{ adults: 2 }]
export const HOTEL_DRAWER_MAX_GUESTS = 12
export const HOTEL_DRAWER_MAX_WIDTH_PX = 1200

function isHotelSearchType(offerType?: string): offerType is App.OfferType {
  return offerType !== undefined && ALL_HOTEL_TYPES.some((hotelSearchType) => hotelSearchType === offerType)
}

function isTileSummaryOffer(
  offer: Awaited<ReturnType<typeof getOffersById>>[number],
): offer is App.HotelOffer | App.BedbankOffer {
  return isLEHotel(offer) || isBedbankOffer(offer)
}

function normalizePlaceName(value?: string) {
  return value?.trim().toLowerCase()
}

function createDestinationSearchItem(placeId: string, label: string): App.SearchDestination {
  return {
    searchType: 'destination',
    value: placeId,
    format: {
      mainText: label,
    },
  }
}

function createPropertySearchItem(propertyId: string, label: string): App.SearchProperty {
  return {
    searchType: 'property',
    value: propertyId,
    format: {
      mainText: label,
    },
  }
}

export type DrawerHotelSearchTarget =
  | { type: 'destination'; placeId: string }
  | { type: 'property'; propertyId: string }

export function getDrawerHotelSearchTarget(searchItem?: App.SearchItem): DrawerHotelSearchTarget | undefined {
  if (searchItem?.searchType === 'property') {
    return { type: 'property', propertyId: searchItem.value }
  }

  if (searchItem?.searchType === 'destination' && searchItem.value !== searchItem.format.mainText) {
    return { type: 'destination', placeId: searchItem.value }
  }

  if (searchItem?.searchType === 'searchItemV3') {
    const { extractedData } = searchItem.searchItem
    if (!extractedData) return undefined

    if (extractedData.searchType === 'property' && extractedData.property?.propertyId) {
      return { type: 'property', propertyId: extractedData.property.propertyId }
    }

    if ('placeIds' in extractedData && extractedData.placeIds?.length) {
      return { type: 'destination', placeId: extractedData.placeIds[0] }
    }
  }

  return undefined
}

function getSearchItemPlaceId(searchItem?: App.SearchItem): string | undefined {
  if (searchItem?.searchType === 'destination' && searchItem.value !== searchItem.format.mainText) {
    return searchItem.value
  }

  if (
    searchItem?.searchType === 'searchItemV3' &&
    'placeIds' in searchItem.searchItem.extractedData &&
    searchItem.searchItem.extractedData.placeIds?.length
  ) {
    return searchItem.searchItem.extractedData.placeIds[0]
  }

  return undefined
}

function getSearchItemName(searchItem?: App.SearchItem): string | undefined {
  return searchItem?.format.mainText
}

function getSearchItemTypeaheadId(searchItem?: App.SearchItem): string | undefined {
  return searchItem?.searchType === 'searchItemV3'
    ? searchItem.searchItem.typeaheadId
    : undefined
}

function getSavedHotelOfferIds(stops: TripItineraryData['stops'] | undefined): Set<string> {
  return new Set(getSavedHotelItemsFromStops(stops).map((item) => item.savedItemData.offerId))
}

function getSavedHotelNights(savedData: SavedTripHotelItem['savedItemData']): number | undefined {
  if (!savedData.checkIn || !savedData.checkOut) return undefined
  return Math.max(1, moment(savedData.checkOut).diff(moment(savedData.checkIn), 'days'))
}

function getSavedHotelLocation(savedData: SavedTripHotelItem['savedItemData'], fallbackLocation: string): string {
  return composeLocationString(savedData.location?.city, savedData.location?.country) || fallbackLocation
}

function getDefaultCheckIn(stopStartDate: string | undefined, contextStop: HotelListingDrawerProps['contextStop']) {
  if (stopStartDate) return stopStartDate
  return contextStop?.days.find(d => d.date)?.date
}

function getDefaultCheckOut(stopEndDate: string | undefined, contextStop: HotelListingDrawerProps['contextStop']) {
  if (stopEndDate) return stopEndDate
  if (!contextStop?.days.length) return undefined
  const datedDays = contextStop.days.filter(d => !!d.date)
  return datedDays.length ? datedDays[datedDays.length - 1].date : undefined
}

export function mapSavedHotelItemToDrawerHotel(item: SavedTripHotelItem, fallbackLocation: string): DrawerHotel {
  const savedData = item.savedItemData
  const savedImages = mapImages(savedData.imageIds ?? [])
  const pricing = savedData.pricing
  const nights = pricing?.durationNights ?? getSavedHotelNights(savedData)
  const pricingFields = mapDrawerHotelPricingFields(pricing, nights, savedData.currencyCode)
  const resolvedPricingFields = pricing
    ? pricingFields
    : {
        ...pricingFields,
        price: mapPrice(savedData.price, getSavedHotelNights(savedData), savedData.currencyCode),
        luxPlusPrice: savedData.memberPrice,
        value: savedData.value,
        discountPercent: savedData.discountPercent,
        memberDiscountPercent: savedData.memberDiscountPercent,
      }

  return {
    id: `saved-${item.id}-${savedData.offerId}`,
    offerId: savedData.offerId,
    offerType: savedData.offerType,
    name: item.name,
    location: getSavedHotelLocation(savedData, fallbackLocation),
    images: savedImages,
    ...resolvedPricingFields,
    saleUnit: savedData.saleUnit,
    checkIn: savedData.checkIn,
    checkOut: savedData.checkOut,
    geoLocation: mapGeoLocation(savedData.location?.geoLocation),
  }
}

// Explore/saved tiles should prefer public-offers images (same source as regular
// hotel search). svc-trip search `imageIds` can diverge after accommodation-
// content enrichment. Until public-offers arrive, keep the existing images.
export function preferPublicOfferImages(
  hotel: DrawerHotel,
  summaryOffer: App.HotelOffer | App.BedbankOffer | undefined,
): DrawerHotel {
  if (!summaryOffer?.images?.length) {
    return hotel
  }

  return {
    ...hotel,
    images: summaryOffer.images,
  }
}

// Explore tiles must use public-offers images. Return undefined while that
// hotel's public-offers lookup is still pending so it is withheld from the list
// instead of flashing the wrong hero image.
export function withPublicOfferTileImages(
  hotel: DrawerHotel,
  summaryOffer: App.HotelOffer | App.BedbankOffer | undefined,
  isPublicOfferLookupPending: boolean,
): DrawerHotel | undefined {
  if (summaryOffer?.images?.length) {
    return {
      ...hotel,
      images: summaryOffer.images,
    }
  }

  if (summaryOffer) {
    // Lookup resolved for this hotel but returned no usable images — fall through
    // to svc-trip rather than withholding (avoids hiding a visible tile during
    // a load-more refetch triggered by other hotels).
    return hotel
  }

  if (isPublicOfferLookupPending) {
    return undefined
  }

  // Lookup finished without images — keep svc-trip as last resort.
  return hotel
}

export function getExploreListLoadingState({
  isHotelSearchLoading,
  isHotelSearchLoadingMore,
  exploreSearchCount,
  visibleExploreCount,
  isPublicOfferLookupPending,
}: {
  isHotelSearchLoading: boolean
  isHotelSearchLoadingMore: boolean
  exploreSearchCount: number
  visibleExploreCount: number
  isPublicOfferLookupPending: boolean
}): { isLoading: boolean; isLoadingMore: boolean } {
  // Hold explore tiles behind one list-level loading state until public-offers
  // images are ready — same idea as regular search waiting on offer fetch.
  const isInitialPublicOfferWait = isPublicOfferLookupPending &&
    exploreSearchCount > 0 &&
    visibleExploreCount === 0
  const isAppendPublicOfferWait = isPublicOfferLookupPending &&
    visibleExploreCount > 0 &&
    exploreSearchCount > visibleExploreCount

  return {
    isLoading: isHotelSearchLoading || isInitialPublicOfferWait,
    isLoadingMore: isHotelSearchLoadingMore || (
      !isHotelSearchLoading && isAppendPublicOfferWait
    ),
  }
}

// Saved itinerary data only carries the fields captured at save time, so a saved
// row can be missing images, rating/reviews, inclusions/amenities, deeplink/map
// metadata, etc. When the same offer is still in the live search results, fill any
// gaps from that tile so saving a hotel doesn't make the visible card regress.
// We never drop a saved row for missing images - the canvas counts saved hotels
// from itinerary data, so hiding one here would cause a "1 saved" / empty-drawer mismatch.
export function applySearchResultNonPriceFallback(hotel: DrawerHotel, fallbackHotelsByOfferId: Map<string, DrawerHotel>): DrawerHotel {
  const fallback = fallbackHotelsByOfferId.get(hotel.offerId)
  if (!fallback) return hotel

  return {
    ...hotel,
    images: hotel.images.length ? hotel.images : fallback.images,
    saleUnit: hotel.saleUnit ?? fallback.saleUnit,
    rating: hotel.rating ?? fallback.rating,
    reviewsTotal: hotel.reviewsTotal ?? fallback.reviewsTotal,
    inclusions: hotel.inclusions?.length ? hotel.inclusions : fallback.inclusions,
    tileAmenities: hotel.tileAmenities?.length ? hotel.tileAmenities : fallback.tileAmenities,
    amenities: hotel.amenities?.length ? hotel.amenities : fallback.amenities,
    deeplinkUrl: hotel.deeplinkUrl ?? fallback.deeplinkUrl,
    mapUrl: hotel.mapUrl ?? fallback.mapUrl,
    geoLocation: hotel.geoLocation ?? fallback.geoLocation,
  }
}

/**
 * The drawer is usually opened for a stop, which always carries both halves.
 * Opened for a bare place -- a breadcrumb, say -- there is no country to add,
 * and a trailing ", " would be searched as part of the destination.
 */
function buildDrawerSearchQuery(placeName: string, placeCountry?: string): string {
  return [placeName, placeCountry].filter(Boolean).join(', ')
}

type UseHotelListingDrawerDataOptions = Pick<
  HotelListingDrawerProps,
  | 'isOpen'
  | 'onClose'
  | 'placeName'
  | 'placeCountry'
  | 'stopStartDate'
  | 'stopEndDate'
  | 'stopCoordinates'
  | 'contextStop'
  | 'itineraryData'
  | 'initialSelectedOfferId'
  | 'initialSelectedOfferType'
>

export default function useHotelListingDrawerData({
  isOpen,
  onClose,
  placeName,
  placeCountry,
  stopStartDate,
  stopEndDate,
  stopCoordinates,
  contextStop,
  itineraryData,
  initialSelectedOfferId,
  initialSelectedOfferType,
}: UseHotelListingDrawerDataOptions) {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const { chatId } = useChatStreamStatus()
  const persistRecentSearch = usePersistTripChatRecentSearch(chatId)
  const eventBus = useTripPlannerEventBusOptional()
  const [selectedOfferId, setSelectedOfferId] = useState<string | undefined>(undefined)
  const [selectedOfferType, setSelectedOfferType] = useState<string | undefined>(undefined)
  // True only when the detail view was reached by selecting a hotel from the
  // listing, so we know whether "back" has a listing to return to. When the drawer
  // is opened straight into a hotel (initialSelectedOfferId, e.g. from the timeline)
  // there is no previous drawer, so the header shows a close (X) instead of Back.
  const [detailFromListing, setDetailFromListing] = useState(false)
  const [selectedHotelName, setSelectedHotelName] = useState<string | undefined>(undefined)
  const [typeaheadId] = useState(() => uuidV4())
  const [drawerSessionKey, setDrawerSessionKey] = useState(() => uuidV4())
  const [searchLocation, setSearchLocation] = useState(() => buildDrawerSearchQuery(placeName, placeCountry))
  const [hotelDateSelection, setHotelDateSelection] = useState<ListingDrawerHotelDateSelection>(() =>
    getDefaultHotelSearchDateSelection(
      getDefaultCheckIn(stopStartDate, contextStop),
      getDefaultCheckOut(stopEndDate, contextStop),
    ),
  )
  const [occupancies, setOccupancies] = useState<Array<App.Occupants>>(() => cloneOccupancies(DEFAULT_OCCUPANCIES))
  const [selectedSearchItem, setSelectedSearchItem] = useState<App.SearchItem | undefined>(undefined)
  const [hasLoadMoreTriggerLeftView, setHasLoadMoreTriggerLeftView] = useState(false)
  const wasOpenRef = useRef(false)
  // False until the open-seed effect applies recent/concrete defaults — avoids
  // persisting a one-frame product-default search before incomplete ages land.
  const [isSearchSessionReady, setIsSearchSessionReady] = useState(false)
  const lastOpenSeedKeyRef = useRef<string | undefined>(undefined)
  const lastOpenSeedSnapshotRef = useRef<{
    location: string
    dateSelection: ListingDrawerHotelDateSelection
    serializedOccupancies: string
    placeId?: string
    propertyId?: string
  } | undefined>(undefined)
  const hasUserLeftOpenSeedRef = useRef(false)
  // Last occupancy applied from resolveSearchDefaults while the drawer is open.
  // When chat/party sync updates itinerary occupancy, re-apply only if the traveller
  // still has this occupancy (so a refined location/dates search does not block the
  // update, and a manual guests edit is not clobbered).
  const lastAppliedResolvedOccupancyRef = useRef<string | undefined>(undefined)
  const hotelDateSelectionRef = useRef(hotelDateSelection)
  hotelDateSelectionRef.current = hotelDateSelection
  const occupanciesRef = useRef(occupancies)
  occupanciesRef.current = occupancies
  const defaultSearchQuery = buildDrawerSearchQuery(placeName, placeCountry)
  const searchQuery = searchLocation.trim() || defaultSearchQuery
  const selectedSearchTarget = useMemo(() => getDrawerHotelSearchTarget(selectedSearchItem), [selectedSearchItem])
  const selectedSearchItemPlaceId = useMemo(() => getSearchItemPlaceId(selectedSearchItem), [selectedSearchItem])
  const selectedSearchItemName = useMemo(() => getSearchItemName(selectedSearchItem), [selectedSearchItem])
  const selectedSearchItemTypeaheadId = useMemo(() => getSearchItemTypeaheadId(selectedSearchItem), [selectedSearchItem])

  useEffect(() => {
    if (!isOpen || !initialSelectedOfferId) return

    setSelectedOfferId(initialSelectedOfferId)
    setSelectedOfferType(initialSelectedOfferType)
    // Opened straight into detail - there is no listing behind it to go back to.
    setDetailFromListing(false)
    setSelectedHotelName(undefined)
  }, [initialSelectedOfferId, initialSelectedOfferType, isOpen])

  const defaultCheckIn = useMemo(() => {
    return getDefaultCheckIn(stopStartDate, contextStop)
  }, [stopStartDate, contextStop])

  const defaultCheckOut = useMemo(() => {
    return getDefaultCheckOut(stopEndDate, contextStop)
  }, [stopEndDate, contextStop])
  const defaultHotelDateSelection = useMemo(
    () => getDefaultHotelSearchDateSelection(defaultCheckIn, defaultCheckOut),
    [defaultCheckIn, defaultCheckOut],
  )
  const resolvedOpenDefaults = useMemo(() => {
    const resolved = resolveSearchDefaults({
      surface: 'hotels',
      stopId: contextStop?.id ?? null,
      recentSearches: itineraryData?.recentSearches,
      indicativeSearchMonth: itineraryData?.indicativeSearchMonth,
      stopIndicativeTravellerParty: contextStop?.indicativeTravellerParty,
      tripIndicativeTravellerParty: itineraryData?.indicativeTravellerParty,
      pastTripTravellerParty: itineraryData?.pastTripTravellerParty,
      concrete: {
        checkIn: defaultCheckIn,
        checkOut: defaultCheckOut,
        locationQuery: defaultSearchQuery,
      },
      productDefaults: { occupancies: DEFAULT_OCCUPANCIES },
    })

    return resolved.surface === 'hotels' ? resolved : undefined
  }, [
    contextStop?.id,
    contextStop?.indicativeTravellerParty,
    defaultCheckIn,
    defaultCheckOut,
    defaultSearchQuery,
    itineraryData?.indicativeSearchMonth,
    itineraryData?.indicativeTravellerParty,
    itineraryData?.pastTripTravellerParty,
    itineraryData?.recentSearches,
  ])
  // Refined = diverged from open seed (recent/concrete/party), not from stop product defaults.
  const openBaselineLocation = resolvedOpenDefaults?.locationQuery ?? defaultSearchQuery
  const openBaselineDateSelection = (
    resolvedOpenDefaults?.dateSelection &&
    isListingDrawerHotelDateSelection(resolvedOpenDefaults.dateSelection)
  )
    ? resolvedOpenDefaults.dateSelection
    : defaultHotelDateSelection
  const openBaselineSerializedOccupancies = useMemo(
    () => serialiseOccupancy(resolvedOpenDefaults?.occupancies ?? DEFAULT_OCCUPANCIES),
    [resolvedOpenDefaults?.occupancies],
  )
  const previousDefaultsRef = useRef({
    hotelDateSelection: defaultHotelDateSelection,
    searchQuery: defaultSearchQuery,
  })
  const searchLocationRef = useRef(searchLocation)
  searchLocationRef.current = searchLocation

  const buildOpenSeed = useCallback((resolved: typeof resolvedOpenDefaults) => {
    const location = resolved?.locationQuery ?? defaultSearchQuery
    const dateSelection = (
      resolved?.dateSelection &&
      isListingDrawerHotelDateSelection(resolved.dateSelection)
    )
      ? resolved.dateSelection
      : defaultHotelDateSelection
    const nextOccupancies = cloneOccupancies(resolved?.occupancies ?? DEFAULT_OCCUPANCIES)
    return {
      location,
      dateSelection,
      occupancies: nextOccupancies,
      serializedOccupancies: serialiseOccupancy(nextOccupancies).join('|'),
      placeId: resolved?.placeId,
      propertyId: resolved?.propertyId,
    }
  }, [defaultHotelDateSelection, defaultSearchQuery])

  const applyOpenSeed = useCallback((seed: ReturnType<typeof buildOpenSeed>) => {
    setSearchLocation(seed.location)
    setHotelDateSelection(seed.dateSelection)
    setOccupancies(seed.occupancies)
    setSelectedSearchItem(
      seed.propertyId
        ? createPropertySearchItem(seed.propertyId, seed.location)
        : seed.placeId
          ? createDestinationSearchItem(seed.placeId, seed.location)
          : undefined,
    )
  }, [])

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setDrawerSessionKey(uuidV4())
      const seed = buildOpenSeed(resolvedOpenDefaults)
      applyOpenSeed(seed)
      lastOpenSeedKeyRef.current = [
        seed.location,
        getHotelSearchDateSelectionKey(seed.dateSelection),
        seed.serializedOccupancies,
        seed.placeId ?? '',
        seed.propertyId ?? '',
      ].join(':')
      lastOpenSeedSnapshotRef.current = seed
      hasUserLeftOpenSeedRef.current = false
      lastAppliedResolvedOccupancyRef.current = seed.serializedOccupancies
      wasOpenRef.current = true
      setIsSearchSessionReady(true)
      previousDefaultsRef.current = {
        hotelDateSelection: defaultHotelDateSelection,
        searchQuery: defaultSearchQuery,
      }
      return
    }

    if (!isOpen) {
      wasOpenRef.current = false
      setIsSearchSessionReady(false)
      lastOpenSeedKeyRef.current = undefined
      lastOpenSeedSnapshotRef.current = undefined
      hasUserLeftOpenSeedRef.current = false
      lastAppliedResolvedOccupancyRef.current = undefined
      previousDefaultsRef.current = {
        hotelDateSelection: defaultHotelDateSelection,
        searchQuery: defaultSearchQuery,
      }
      return
    }

    // recentSearches (or other open defaults) can arrive after the first seed. If the
    // traveller hasn't left that seed, re-apply so auto-persist doesn't overwrite a
    // real recent search with concrete stop defaults from the cold-open frame.
    const nextSeed = buildOpenSeed(resolvedOpenDefaults)
    const nextSeedKey = [
      nextSeed.location,
      getHotelSearchDateSelectionKey(nextSeed.dateSelection),
      nextSeed.serializedOccupancies,
      nextSeed.placeId ?? '',
      nextSeed.propertyId ?? '',
    ].join(':')
    const previousSeed = lastOpenSeedSnapshotRef.current
    if (
      !hasUserLeftOpenSeedRef.current &&
      previousSeed &&
      nextSeedKey !== lastOpenSeedKeyRef.current
    ) {
      const stillOnPreviousSeed =
        normalizePlaceName(searchLocationRef.current) === normalizePlaceName(previousSeed.location) &&
        areHotelSearchDateSelectionsEqual(
          hotelDateSelectionRef.current,
          previousSeed.dateSelection,
        ) &&
        serialiseOccupancy(occupanciesRef.current).join('|') === previousSeed.serializedOccupancies

      if (stillOnPreviousSeed) {
        applyOpenSeed(nextSeed)
        lastOpenSeedKeyRef.current = nextSeedKey
        lastOpenSeedSnapshotRef.current = nextSeed
        lastAppliedResolvedOccupancyRef.current = nextSeed.serializedOccupancies
      } else {
        hasUserLeftOpenSeedRef.current = true
        lastOpenSeedKeyRef.current = nextSeedKey
      }
    }

    // Party / recent occupancy can change from chat while the drawer stays open
    // (often after the traveller refined location/dates). If guests still match the
    // last applied resolved occupancy, update guests only so auto-persist cannot
    // clobber a backend party sync with stale React state.
    const previousAppliedOccupancy = lastAppliedResolvedOccupancyRef.current
    const nextResolvedOccupancy = nextSeed.serializedOccupancies
    if (
      previousAppliedOccupancy !== undefined &&
      nextResolvedOccupancy !== previousAppliedOccupancy &&
      serialiseOccupancy(occupanciesRef.current).join('|') === previousAppliedOccupancy
    ) {
      setOccupancies(nextSeed.occupancies)
      if (lastOpenSeedSnapshotRef.current) {
        lastOpenSeedSnapshotRef.current = {
          ...lastOpenSeedSnapshotRef.current,
          serializedOccupancies: nextResolvedOccupancy,
        }
      }
      // Stay aligned with current resolved defaults (not the pre-refine snapshot location).
      lastOpenSeedKeyRef.current = nextSeedKey
    }
    lastAppliedResolvedOccupancyRef.current = nextResolvedOccupancy

    const previousDefaults = previousDefaultsRef.current
    const searchQueryChanged =
      normalizePlaceName(previousDefaults.searchQuery) !== normalizePlaceName(defaultSearchQuery)
    const datesChanged = !areHotelSearchDateSelectionsEqual(
      previousDefaults.hotelDateSelection,
      defaultHotelDateSelection,
    )

    if (searchQueryChanged &&
      normalizePlaceName(searchLocationRef.current) === normalizePlaceName(previousDefaults.searchQuery)
    ) {
      setSearchLocation(defaultSearchQuery)
      setSelectedSearchItem(undefined)
    }

    if (datesChanged) {
      setHotelDateSelection((currentSelection) =>
        areHotelSearchDateSelectionsEqual(currentSelection, previousDefaults.hotelDateSelection)
          ? defaultHotelDateSelection
          : currentSelection,
      )
    }

    previousDefaultsRef.current = {
      hotelDateSelection: defaultHotelDateSelection,
      searchQuery: defaultSearchQuery,
    }
  }, [
    applyOpenSeed,
    buildOpenSeed,
    defaultHotelDateSelection,
    defaultSearchQuery,
    isOpen,
    resolvedOpenDefaults,
  ])

  const { checkIn, checkOut, flexibleMonths, flexibleNights } = getHotelSearchDateParams(hotelDateSelection)
  const serializedOccupancies = useMemo(() => serialiseOccupancy(occupancies), [occupancies])
  // '|' separates rooms (matches the other serialisations in this file, e.g.
  // serializedOccupancies above); ',' is already used inside a room's own
  // children ages, so joining rooms with ',' is ambiguous for multi-room
  // searches — svc-trip's compact parser splits on '|' only.
  const offerDetailOccupancies = serializedOccupancies.join('|')
  const isDefaultDestinationSearch = normalizePlaceName(searchQuery) === normalizePlaceName(openBaselineLocation)
  const hotelSearchQuery = isDefaultDestinationSearch
    ? `Hotels in ${openBaselineLocation}`
    : searchQuery
  const isRefinedSearch = !isDefaultDestinationSearch ||
    !areHotelSearchDateSelectionsEqual(hotelDateSelection, openBaselineDateSelection) ||
    serializedOccupancies.join('|') !== openBaselineSerializedOccupancies.join('|')
  const headerTitle = isRefinedSearch ?
      intl.formatMessage({
        id: 'uDktRbWrV6na2m15vYXA',
        defaultMessage: 'Search hotels',
        description: 'Generic title for hotel listing drawer after users refine the drawer search.',
      }) :
    undefined

  const filterNights = useMemo(() => {
    if (!checkIn || !checkOut) return undefined
    const nights = moment(checkOut).diff(moment(checkIn), 'days')
    return nights > 0 ? nights : undefined
  }, [checkIn, checkOut])

  const { data: destinationPlace, isFetched: isDestinationPlaceFetched } = useSearchPlace(
    {
      lat: stopCoordinates?.lat,
      lng: stopCoordinates?.lng,
      type: 'city',
    },
    {
      enabled: isOpen && !!stopCoordinates,
      staleTime: Infinity,
    },
  )

  const defaultDestinationPlaceId = isDefaultDestinationSearch ? destinationPlace?.id : undefined
  const searchPlaceId = selectedSearchTarget?.type === 'property'
    ? undefined
    : selectedSearchItemPlaceId ?? defaultDestinationPlaceId
  const searchPropertyId = selectedSearchTarget?.type === 'property'
    ? selectedSearchTarget.propertyId
    : undefined
  const hasUnresolvedDestinationSearch = !isDefaultDestinationSearch &&
    !!searchQuery.trim() &&
    !searchPlaceId &&
    !searchPropertyId
  const isHotelSearchReady = !!searchPropertyId || !isDefaultDestinationSearch || !stopCoordinates || isDestinationPlaceFetched

  const {
    hotels,
    sections: hotelSearchSections,
    isLoading: isHotelSearchLoading,
    isError: isSearchError,
    hasMore,
    isLoadingMore: isHotelSearchLoadingMore,
    loadMore,
    refetch: refetchSearch,
  } = useHotelSearch({
    searchQuery: hotelSearchQuery,
    placeId: searchPlaceId,
    propertyId: searchPropertyId,
    typeaheadId: selectedSearchItemTypeaheadId,
    region: geo.currentRegionCode,
    checkIn,
    checkOut,
    flexibleMonths,
    flexibleNights,
    occupancies,
    // Incomplete child ages (e.g. inherited from flights) must not price hotels.
    enabled: isOpen && isHotelSearchReady && isValidOccupancy(...occupancies),
  })
  const hotelOfferIds = useMemo(() => hotels.map((hotel) => hotel.offerId), [hotels])
  const shouldLoadTileSummaryOffers = hotelDateSelection.type !== 'flexible'
  const {
    data: tileOffers = [],
    isFetching: isTileOffersFetching,
  } = useQuery({
    queryKey: OfferKeys.collection(geo.currentRegionCode, [
      checkIn ?? '',
      checkOut ?? '',
      flexibleMonths ?? '',
      flexibleNights ?? '',
      serializedOccupancies.join('|'),
      ...hotelOfferIds,
    ]),
    queryFn: () => getOffersById(
      hotelOfferIds,
      geo.currentRegionCode,
      {
        checkIn,
        checkOut,
        occupancies,
      },
    ),
    enabled: isOpen && hotelOfferIds.length > 0 && shouldLoadTileSummaryOffers,
    staleTime: OFFER_DETAIL_STALE_TIME,
    keepPreviousData: shouldLoadTileSummaryOffers,
  })
  // Pending only while a fetch for the current explore offer IDs is in flight.
  // Hotels that already have matching public-offer images (e.g. page 1 during
  // load-more with keepPreviousData) still render via withPublicOfferTileImages.
  const isPublicOfferLookupPending = hotelOfferIds.length > 0 && isTileOffersFetching
  const tileSummaryOffers = useMemo<Array<App.HotelOffer | App.BedbankOffer>>(
    () => shouldLoadTileSummaryOffers ? tileOffers.filter(isTileSummaryOffer) : [],
    [shouldLoadTileSummaryOffers, tileOffers],
  )
  const trackingOfferById = useMemo(() => {
    return new Map(tileSummaryOffers.map((offer) => [offer.id, offer]))
  }, [tileSummaryOffers])
  const tileDetailHotels = useMemo(
    () => hotels.filter((hotel): hotel is DrawerHotel & { offerType: App.OfferType } => isHotelSearchType(hotel.offerType)),
    [hotels],
  )
  const tileDetailQueries = useQueries({
    queries: tileDetailHotels.map((hotel) => ({
      queryKey: OfferDetailKeys.detail(
        hotel.offerId,
        geo.currentRegionCode,
        undefined,
        hotel.offerType,
        checkIn,
        checkOut,
        offerDetailOccupancies,
        flexibleMonths,
        flexibleNights,
      ),
      queryFn: () => getOfferDetail({
        offerId: hotel.offerId,
        region: geo.currentRegionCode,
        offerType: hotel.offerType,
        checkIn,
        checkOut,
        occupancies: offerDetailOccupancies,
        flexibleMonths,
        flexibleNights,
      }),
      enabled: isOpen,
      staleTime: OFFER_DETAIL_STALE_TIME,
    })),
  })
  const hotelsWithTileDetails = useMemo(() => {
    const summaryTileFeaturesByOfferId = new Map<string, Pick<DrawerHotel, 'inclusions' | 'tileAmenities'>>(
      tileSummaryOffers.map((offer) => [
        offer.id,
        mapOfferToDrawerHotelTileFeatures(offer),
      ]),
    )
    const detailsByOfferId = new Map<string, Pick<DrawerHotel, 'rating' | 'reviewsTotal' | 'inclusions' | 'amenities'>>()

    tileDetailQueries.forEach((query, index) => {
      const hotel = tileDetailHotels[index]
      if (!hotel || !query.data) {
        return
      }

      const detailHotel = mergeDrawerHotelWithOfferDetail(hotel, query.data)

      detailsByOfferId.set(hotel.offerId, {
        rating: detailHotel.rating,
        reviewsTotal: detailHotel.reviewsTotal,
        inclusions: detailHotel.inclusions,
        amenities: detailHotel.amenities,
      })
    })

    return hotels.flatMap((hotel) => {
      const summaryTileFeatures = summaryTileFeaturesByOfferId.get(hotel.offerId)
      const details = detailsByOfferId.get(hotel.offerId)
      const enrichedHotel = details || summaryTileFeatures ? {
        ...hotel,
        rating: details?.rating ?? hotel.rating,
        reviewsTotal: details?.reviewsTotal ?? hotel.reviewsTotal,
        inclusions: details?.inclusions?.length ? details.inclusions : summaryTileFeatures?.inclusions ?? hotel.inclusions,
        tileAmenities: summaryTileFeatures?.tileAmenities ?? hotel.tileAmenities,
        amenities: details?.amenities ?? hotel.amenities,
      } : hotel

      const hotelWithImages = withPublicOfferTileImages(
        enrichedHotel,
        trackingOfferById.get(hotel.offerId),
        isPublicOfferLookupPending,
      )

      return hotelWithImages ? [hotelWithImages] : []
    })
  }, [hotels, isPublicOfferLookupPending, tileDetailHotels, tileDetailQueries, tileSummaryOffers, trackingOfferById])

  const savedHotelStops = useMemo(() => {
    if (isRefinedSearch) return []
    return contextStop ? [contextStop] : itineraryData?.stops
  }, [contextStop, isRefinedSearch, itineraryData?.stops])

  const savedHotelDetailHotels = useMemo(
    () =>
      getSavedHotelItemsFromStops(savedHotelStops)
        .map((item) => ({ offerId: item.savedItemData.offerId, offerType: item.savedItemData.offerType }))
        .filter((hotel) => isHotelSearchType(hotel.offerType)),
    [savedHotelStops],
  )

  const savedDetailQueries = useQueries({
    queries: savedHotelDetailHotels.map((hotel) => ({
      queryKey: OfferDetailKeys.detail(
        hotel.offerId,
        geo.currentRegionCode,
        undefined,
        hotel.offerType,
        checkIn,
        checkOut,
        offerDetailOccupancies,
        flexibleMonths,
        flexibleNights,
      ),
      queryFn: () => getOfferDetail({
        offerId: hotel.offerId,
        region: geo.currentRegionCode,
        offerType: hotel.offerType,
        checkIn,
        checkOut,
        occupancies: offerDetailOccupancies,
        flexibleMonths,
        flexibleNights,
      }),
      enabled: isOpen,
      staleTime: OFFER_DETAIL_STALE_TIME,
    })),
  })

  const savedHotelDetailDrawerById = useMemo(() => {
    const detailsByOfferId = new Map<string, DrawerHotel>()
    savedDetailQueries.forEach((query, index) => {
      const hotel = savedHotelDetailHotels[index]
      if (!hotel || !query.data) return
      detailsByOfferId.set(hotel.offerId, mapOfferDetailToDrawerHotel(query.data))
    })
    return detailsByOfferId
  }, [savedDetailQueries, savedHotelDetailHotels])

  const savedDrawerHotels = useMemo(() => {
    const fallbackLocation = [placeName, placeCountry].filter(Boolean).join(', ')
    const fallbackHotelsByOfferId = new Map(savedHotelDetailDrawerById)
    hotelsWithTileDetails.forEach((hotel) => fallbackHotelsByOfferId.set(hotel.offerId, hotel))
    return getSavedHotelItemsFromStops(savedHotelStops)
      .map((item) => mapSavedHotelItemToDrawerHotel(item, fallbackLocation))
      .map((hotel) => applySearchResultNonPriceFallback(hotel, fallbackHotelsByOfferId))
      .map((hotel) => preferPublicOfferImages(hotel, trackingOfferById.get(hotel.offerId)))
  }, [hotelsWithTileDetails, placeCountry, placeName, savedHotelStops, savedHotelDetailDrawerById, trackingOfferById])

  const savedHotelOfferIds = useMemo(() => {
    return getSavedHotelOfferIds(savedHotelStops)
  }, [savedHotelStops])

  const orderedHotelsWithTileDetails = useMemo(() => {
    return hotelsWithTileDetails.filter((hotel) => !savedHotelOfferIds.has(hotel.offerId))
  }, [hotelsWithTileDetails, savedHotelOfferIds])

  const exploreSearchHotels = useMemo(
    () => hotels.filter((hotel) => !savedHotelOfferIds.has(hotel.offerId)),
    [hotels, savedHotelOfferIds],
  )

  const { isLoading, isLoadingMore } = getExploreListLoadingState({
    isHotelSearchLoading,
    isHotelSearchLoadingMore,
    exploreSearchCount: exploreSearchHotels.length,
    visibleExploreCount: orderedHotelsWithTileDetails.length,
    isPublicOfferLookupPending,
  })

  const orderedHotelSearchSections = useMemo<Array<HotelSearchSection>>(() => {
    const visibleOfferIds = new Set(orderedHotelsWithTileDetails.map((hotel) => hotel.offerId))
    const visibleSearchResultPositions = hotels
      .map((hotel, index) => visibleOfferIds.has(hotel.offerId) ? index : undefined)
      .filter((index): index is number => index !== undefined)

    const visibleSections = hotelSearchSections
      .map((section) => ({
        ...section,
        position: visibleSearchResultPositions.filter((position) => position < section.position).length,
      }))
      .filter((section) => section.position < orderedHotelsWithTileDetails.length)

    return uniqueBy(
      visibleSections,
      (section) => `${section.type}:${section.position}`,
    )
  }, [
    hotelSearchSections,
    hotels,
    orderedHotelsWithTileDetails,
  ])

  const savedBooking = useMemo(
    () => getOfferSavedBooking(selectedOfferId, itineraryData),
    [selectedOfferId, itineraryData],
  )

  const { savedBookedRooms, detailOrderId, orderIdForLink } = useOfferBookingState(
    selectedOfferId ?? undefined,
    itineraryData,
  )

  const { offerDetail, isLoading: isDetailLoading, isError: isDetailError, refetch: refetchDetail } = useOfferDetails({
    offerId: selectedOfferId,
    region: geo.currentRegionCode,
    offerType: selectedOfferType,
    checkIn,
    checkOut,
    flexibleMonths,
    flexibleNights,
    occupancies: offerDetailOccupancies,
    orderId: detailOrderId,
    enabled: isOpen && !!selectedOfferId,
  })

  const { reviews, isLoading: reviewsLoading } = useOfferReviews({
    offerId: selectedOfferId,
    region: geo.currentRegionCode,
    enabled: !!selectedOfferId && !!offerDetail,
  })

  const detailHotel = useMemo(() => {
    if (!offerDetail) return undefined
    return mapOfferDetailToDrawerHotel(offerDetail)
  }, [offerDetail])

  // offer_drawer_opened dispatch lives in HotelDetailView now (the component this hook's
  // `detailHotel` eventually renders into via HotelListingDrawer.tsx), not here -- see its own
  // comment for why that's the single choke point.

  const selectedHotelTitle = useMemo(() => {
    if (!selectedOfferId) return undefined
    return detailHotel?.name ??
      selectedHotelName ??
      savedDrawerHotels.find((hotel) => hotel.offerId === selectedOfferId)?.name ??
      orderedHotelsWithTileDetails.find((hotel) => hotel.offerId === selectedOfferId)?.name
  }, [
    detailHotel?.name,
    orderedHotelsWithTileDetails,
    savedDrawerHotels,
    selectedHotelName,
    selectedOfferId,
  ])

  const handleSelectHotel = useCallback((hotel: DrawerHotel) => {
    setSelectedOfferId(hotel.offerId)
    setSelectedOfferType(hotel.offerType)
    setDetailFromListing(true)
    setSelectedHotelName(hotel.name)
  }, [])

  const handleBack = useCallback(() => {
    setSelectedOfferId(undefined)
    setSelectedOfferType(undefined)
    setSelectedHotelName(undefined)
    setDetailFromListing(false)
  }, [])

  const handleClose = useCallback(() => {
    setSelectedOfferId(undefined)
    setSelectedOfferType(undefined)
    setSelectedHotelName(undefined)
    setDetailFromListing(false)
    onClose()
  }, [onClose])

  const handleAfterClose = useCallback(() => {
    setSelectedOfferId(undefined)
    setSelectedOfferType(undefined)
    setSelectedHotelName(undefined)
    setDetailFromListing(false)
    setHasLoadMoreTriggerLeftView(false)
  }, [])

  const tracking = useTripPlannerOfferListTracking(
    'hotelSearchDrawer',
    `${drawerSessionKey}:${searchQuery}:${getHotelSearchDateSelectionKey(hotelDateSelection)}:${serializedOccupancies.join('|')}`,
  )

  const browseAllResultsUrl = useMemo(() => {
    const destinationId = selectedSearchTarget?.type === 'destination'
      ? selectedSearchTarget.placeId
      : selectedSearchTarget?.type === 'property'
        ? undefined
        : defaultDestinationPlaceId
    const destinationName = selectedSearchItemName ?? (destinationId ? searchQuery : undefined)
    const propertySearchItem = selectedSearchTarget?.type === 'property'
      ? createPropertySearchItem(selectedSearchTarget.propertyId, selectedSearchItemName ?? searchQuery)
      : undefined
    const specificDates = hotelDateSelection.type === 'specific'
      ? { checkIn: hotelDateSelection.checkIn, checkOut: hotelDateSelection.checkOut }
      : undefined
    const searchParams = encodeSearchParams({
      urlSearch: '',
      dates: specificDates,
      rooms: occupancies,
      flexibleMonths,
      flexibleNights,
      userSelectedFlexibleMonths: hotelDateSelection.type === 'flexible'
        ? hotelDateSelection.userSelectedFlexibleMonths
        : undefined,
      searchItem: propertySearchItem,
      destinationId,
      destinationName,
    })

    if (!destinationId && !propertySearchItem) {
      searchParams.set('query', searchQuery)
      searchParams.set(queryKeyTypeaheadId, typeaheadId)
    } else if (propertySearchItem && selectedSearchItemTypeaheadId) {
      searchParams.set(queryKeyTypeaheadId, selectedSearchItemTypeaheadId)
    }

    return buildUrl('/search', geo.currentRegionCode, searchParams.toString())
  }, [defaultDestinationPlaceId, flexibleMonths, flexibleNights, geo.currentRegionCode, hotelDateSelection, occupancies, searchQuery, selectedSearchItemName, selectedSearchItemTypeaheadId, selectedSearchTarget, typeaheadId])

  const hasSavedHotels = savedDrawerHotels.length > 0
  const hasHotels = hasSavedHotels || orderedHotelsWithTileDetails.length > 0
  const hasExploreHotelContent = isLoading || orderedHotelsWithTileDetails.length > 0
  const canShowExploreSection = hasExploreHotelContent || hasMore
  const isDetailView = !!selectedOfferId
  // Only offer "Back" when the detail was reached from the listing; a directly
  // opened detail shows a close (X) that returns to the timeline.
  const canGoBack = isDetailView && detailFromListing
  const [loadMoreTriggerRef, isLoadMoreTriggerInView] = useInView({
    threshold: 0,
    rootMargin: '0px 0px 160px 0px',
  })

  useEffect(() => {
    if (isDetailView || !hasMore) {
      setHasLoadMoreTriggerLeftView(false)
      return
    }

    if (!isLoadMoreTriggerInView && !isLoadingMore) {
      setHasLoadMoreTriggerLeftView(true)
    }
  }, [hasMore, isDetailView, isLoadMoreTriggerInView, isLoadingMore])

  useEffect(() => {
    if (!isDetailView && hasMore && !isLoadingMore && isLoadMoreTriggerInView && hasLoadMoreTriggerLeftView) {
      setHasLoadMoreTriggerLeftView(false)
      loadMore()
    }
  }, [hasLoadMoreTriggerLeftView, hasMore, isDetailView, isLoadMoreTriggerInView, isLoadingMore, loadMore])

  const dayRangeLabel = useMemo(() => {
    if (!selectedOfferId) return undefined
    const range = getOfferItineraryDayRange(selectedOfferId, itineraryData)
    return range?.label
  }, [selectedOfferId, itineraryData])

  // Persist whatever is actually driving hotel results — not only Search clicks.
  // Date Apply and drawer-open auto-search update parent state (and results) without
  // going through onSearchSubmit; without this, switching stops/dates silently skips
  // TripChatRecentSearch (e.g. London Search saved, Paris date-refine did not).
  const lastPersistedHotelSearchKeyRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!isOpen) {
      lastPersistedHotelSearchKeyRef.current = undefined
      return
    }
    if (!isSearchSessionReady) return
    if (!isHotelSearchReady || hasUnresolvedDestinationSearch) return
    if (!isValidOccupancy(...occupancies)) return

    const key = [
      contextStop?.id ?? 'null',
      searchQuery,
      getHotelSearchDateSelectionKey(hotelDateSelection),
      serializedOccupancies.join('|'),
      searchPlaceId ?? '',
      searchPropertyId ?? '',
    ].join(':')

    if (lastPersistedHotelSearchKeyRef.current === key) return
    lastPersistedHotelSearchKeyRef.current = key

    persistRecentSearch(buildHotelRecentSearchUpsertBody({
      tripStopId: contextStop?.id ?? null,
      searchQuery,
      placeId: searchPlaceId,
      propertyId: searchPropertyId,
      dateSelection: hotelDateSelection,
      occupancies,
    }))
  }, [
    contextStop?.id,
    hasUnresolvedDestinationSearch,
    hotelDateSelection,
    isHotelSearchReady,
    isOpen,
    isSearchSessionReady,
    occupancies,
    persistRecentSearch,
    searchPlaceId,
    searchPropertyId,
    searchQuery,
    serializedOccupancies,
  ])

  const handleSearchSubmit = useCallback((params: ListingDrawerSearchSubmitParams) => {
    const target = getDrawerHotelSearchTarget(params.searchItem)

    persistRecentSearch(buildHotelRecentSearchUpsertBody({
      tripStopId: contextStop?.id ?? null,
      searchQuery: params.searchValue,
      placeId: target?.type === 'destination' ? target.placeId : undefined,
      propertyId: target?.type === 'property' ? target.propertyId : undefined,
      dateSelection: params.hotelDateSelection,
      occupancies: params.occupancies,
    }))
    eventBus?.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildHotelSearchSubmittedActivityEntry(params.searchValue, params.hotelDateSelection, params.occupancies)],
    })
    // No stopless case here (unlike flight search) -- this drawer is only ever opened with a
    // stop in context, so there's no flat-key fallback to fall back to.
    if (contextStop?.id !== undefined) {
      eventBus?.dispatch({
        type: 'trip_planner_committed_state',
        entries: [buildLastHotelSearchCommittedStateEntry(contextStop.id, params.searchValue, params.hotelDateSelection, params.occupancies)],
      })
    }
  }, [contextStop?.id, eventBus, persistRecentSearch])

  const searchCalendarFocusDate = useMemo(() => {
    const resolved = resolveSearchDefaults({
      surface: 'hotels',
      stopId: contextStop?.id ?? null,
      recentSearches: itineraryData?.recentSearches,
      indicativeSearchMonth: itineraryData?.indicativeSearchMonth,
      stopIndicativeTravellerParty: contextStop?.indicativeTravellerParty,
      tripIndicativeTravellerParty: itineraryData?.indicativeTravellerParty,
      pastTripTravellerParty: itineraryData?.pastTripTravellerParty,
      selected: { dateSelection: hotelDateSelection },
      concrete: {
        checkIn: defaultCheckIn,
        checkOut: defaultCheckOut,
      },
      productDefaults: { occupancies: DEFAULT_OCCUPANCIES },
    })

    return resolved.surface === 'hotels' ? resolved.focusMonth : new Date()
  }, [
    contextStop?.id,
    contextStop?.indicativeTravellerParty,
    defaultCheckIn,
    defaultCheckOut,
    hotelDateSelection,
    itineraryData?.indicativeSearchMonth,
    itineraryData?.indicativeTravellerParty,
    itineraryData?.pastTripTravellerParty,
    itineraryData?.recentSearches,
  ])

  return {
    browseAllResultsUrl,
    canShowExploreSection,
    checkIn,
    checkOut,
    searchCalendarFocusDate,
    flexibleMonths,
    flexibleNights,
    hotelDateSelection,
    dayRangeLabel,
    detailHotel,
    filterNights,
    handleAfterClose,
    handleBack,
    handleClose,
    handleSearchSubmit,
    handleSelectHotel,
    hasHotels,
    hasMore,
    hasSavedHotels,
    hasUnresolvedDestinationSearch,
    headerTitle,
    selectedHotelTitle,
    selectedOfferId,
    isBooked: isOfferBookedInItinerary(selectedOfferId ?? undefined, itineraryData),
    canGoBack,
    isDetailError,
    isDetailLoading,
    isDetailView,
    isLoading,
    isSearchError,
    isLoadingMore,
    loadMore,
    loadMoreTriggerRef,
    occupancies,
    drawerSessionKey,
    orderIdForLink,
    refetchDetail,
    refetchSearch,
    reviews,
    reviewsLoading,
    savedBookedRooms,
    savedBooking,
    savedDrawerHotels,
    savedHeading: intl.formatMessage({
      id: 'KlCMmpXwaYn9wnS-xRP-',
      defaultMessage: 'Saved',
      description: 'Section heading for saved hotels in the trip planner hotel drawer',
    }),
    searchLocation,
    setOccupancies,
    setSearchLocation,
    setHotelDateSelection,
    setSelectedSearchItem,
    orderedHotelsWithTileDetails,
    orderedHotelSearchSections,
    tracking,
    trackingOfferById,
    exploreHeading: intl.formatMessage({
      id: 'jUY_ikacrLCymlSbEXow',
      defaultMessage: 'Explore more',
      description: 'Section heading above hotel search results in the trip planner hotel drawer',
    }),
  }
}
